'use client';

import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  Copy,
  ExternalLink,
  Instagram,
  Package,
  Search,
  ShieldCheck,
  ShoppingBag,
} from 'lucide-react';
import { PublicLayout } from '@/components/Layout/PublicLayout';
import { Loader } from '@/components/ui/loader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { usePublicMerchQuery } from '@/hooks/api/useMerch';
import { MerchProductCard } from '../components/MerchProductCard';
import { MerchStoreTheme } from '../components/MerchStoreTheme';
import type { PublicMerchStore } from '../interfaces/merch';
import { toast } from 'sonner';

interface PublicMerchStoreProps {
  slug: string;
  initialData?: PublicMerchStore;
}

const SOCIAL_LINKS = [
  { key: 'instagram' as const, label: 'Instagram', icon: Instagram },
  { key: 'twitch' as const, label: 'Twitch', prefix: 'https://twitch.tv/' },
  { key: 'kick' as const, label: 'Kick', prefix: 'https://kick.com/' },
  { key: 'tiktok' as const, label: 'TikTok', prefix: 'https://tiktok.com/@' },
  { key: 'x' as const, label: 'X', prefix: 'https://x.com/' },
];

export function PublicMerchStore({ slug, initialData }: PublicMerchStoreProps) {
  const { data, isLoading } = usePublicMerchQuery(slug, initialData);
  const [search, setSearch] = useState('');

  const store = data ?? initialData;
  const organization = store?.organization;
  const products = store?.products ?? [];

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return products;
    return products.filter(
      (product) =>
        product.name.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query),
    );
  }, [products, search]);

  const inStockCount = products.filter((p) => p.stock_quantity > 0).length;

  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      const aFeatured = a.details?.is_featured ? 1 : 0;
      const bFeatured = b.details?.is_featured ? 1 : 0;
      return bFeatured - aFeatured;
    });
  }, [filteredProducts]);

  const copyStoreLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  if (isLoading && !store) {
    return (
      <PublicLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader text="Cargando tienda..." />
        </div>
      </PublicLayout>
    );
  }

  if (!organization) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-7xl px-4 py-24 text-center">
          <Package className="mx-auto h-12 w-12 text-muted-foreground/40" />
          <h1 className="mt-4 text-2xl font-semibold">Tienda no encontrada</h1>
          <p className="mt-2 text-muted-foreground">
            Esta organización no existe o no tiene tienda pública activa.
          </p>
        </div>
      </PublicLayout>
    );
  }

  const socialItems = SOCIAL_LINKS.flatMap(({ key, label, icon, prefix }) => {
    const handle = organization[key];
    if (!handle) return [];
    const href = handle.startsWith('http') ? handle : `${prefix ?? ''}${handle.replace(/^@/, '')}`;
    return [{ key, label, href, icon }];
  });

  return (
    <PublicLayout>
      <MerchStoreTheme organization={organization}>
        <section className="relative overflow-hidden border-b border-[color-mix(in_srgb,var(--merch-accent)_18%,transparent)]">
          <div
            className="pointer-events-none absolute inset-0 opacity-90"
            style={{
              background: `
                radial-gradient(ellipse 80% 60% at 10% -10%, color-mix(in srgb, var(--merch-accent) 22%, transparent), transparent 55%),
                radial-gradient(ellipse 60% 50% at 100% 0%, color-mix(in srgb, var(--merch-accent-secondary) 16%, transparent), transparent 50%),
                linear-gradient(to bottom, hsl(var(--background)), hsl(var(--background)))
              `,
            }}
          />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
            <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="space-y-6">
                <div className="flex flex-wrap items-center gap-3">
                  <Badge
                    variant="outline"
                    className="border-[color-mix(in_srgb,var(--merch-accent)_35%,transparent)] text-[var(--merch-accent)]"
                  >
                    Merch oficial
                  </Badge>
                  {products.length > 0 && (
                    <Badge variant="secondary">{inStockCount} en stock</Badge>
                  )}
                </div>

                <div className="flex items-start gap-5">
                  {organization.logo_url ? (
                    <img 
                      src={organization.logo_url} 
                      alt={organization.name} 
                      width={80}
                      height={80}
                      loading="lazy"
                      className="rounded-2xl border border-white/10 object-contain shadow-lg shadow-black/20" 
                    />
                  ) : (
                    <div
                      className="flex h-20 w-20 items-center justify-center rounded-2xl border border-white/10 shadow-lg"
                      style={{
                        background: `color-mix(in srgb, var(--merch-accent) 18%, transparent)`,
                      }}
                    >
                      <ShoppingBag className="h-9 w-9 text-[var(--merch-accent)]" />
                    </div>
                  )}
                  <div>
                    <h1 className="font-[family-name:var(--font-merch-display)] text-4xl font-bold uppercase tracking-tight sm:text-5xl lg:text-6xl">
                      {organization.name}
                    </h1>
                    <p className="mt-3 max-w-xl text-base text-muted-foreground sm:text-lg">
                      Productos oficiales con envío coordinado por la organización. Pago seguro vía
                      Mercado Pago.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                    <ShieldCheck className="h-4 w-4 text-[var(--merch-accent)]" />
                    Checkout Pro · Mercado Pago
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2 border-[color-mix(in_srgb,var(--merch-accent)_25%,transparent)]"
                    onClick={() => void copyStoreLink()}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Copiar enlace
                  </Button>
                </div>

                {socialItems.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {socialItems.map(({ key, label, href, icon: Icon }) => (
                      <Button key={key} variant="ghost" size="sm" className="h-8 gap-2 px-3" asChild>
                        <Link to={href} target="_blank" rel="noopener noreferrer">
                          {Icon ? <Icon className="h-3.5 w-3.5" /> : <ExternalLink className="h-3.5 w-3.5" />}
                          {label}
                        </Link>
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              <div className="hidden lg:block">
                <div
                  className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-white/10 p-8 shadow-2xl"
                  style={{
                    background: `linear-gradient(145deg, color-mix(in srgb, var(--merch-accent) 12%, transparent), transparent 60%), hsl(var(--card)/0.4)`,
                  }}
                >
                  <div
                    className="absolute -right-8 -top-8 h-40 w-40 rounded-full blur-3xl opacity-60"
                    style={{ background: 'var(--merch-accent)' }}
                  />
                  <div className="relative flex h-full flex-col justify-end">
                    <p className="text-sm uppercase tracking-[0.25em] text-muted-foreground">
                      Colección
                    </p>
                    <p className="mt-2 text-3xl font-semibold">
                      {products.length > 0 ? `${products.length} piezas` : 'Próximamente'}
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Edición oficial · Stock limitado
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {products.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-[color-mix(in_srgb,var(--merch-accent)_25%,transparent)] bg-card/30 p-16 text-center">
              <ShoppingBag className="mx-auto h-12 w-12 text-muted-foreground/40" />
              <h2 className="mt-4 text-xl font-semibold">Próximamente</h2>
              <p className="mt-2 text-muted-foreground">
                {organization.name} aún no ha publicado productos en su tienda.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold">Catálogo</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Selecciona un producto para comprar con Mercado Pago
                  </p>
                </div>
                {products.length > 3 && (
                  <div className="relative w-full sm:max-w-xs">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Buscar producto..."
                      className="pl-9"
                    />
                  </div>
                )}
              </div>

              {sortedProducts.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">
                  No hay productos que coincidan con &quot;{search}&quot;
                </p>
              ) : (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {sortedProducts.map((product, index) => (
                    <MerchProductCard
                      key={product.id}
                      product={product}
                      storeSlug={slug}
                      featured={
                        Boolean(product.details?.is_featured) ||
                        (index === 0 && sortedProducts.length > 1 && !product.details?.is_featured)
                      }
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </MerchStoreTheme>
    </PublicLayout>
  );
}
